import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
  setWaitingForReply,
  setQuestions,
  completeThinkingStep,
  addMessage,
} from "@/store/slices/aiChatSlice";
import { reply } from "@/services/aiService";
import { setError } from "@/store/slices/aiChatSlice";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { Check, ChevronRight, Loader2 } from "lucide-react";
import type { Question } from "@/store/slices/aiChatSlice";

interface ClarificationPanelProps {
  questions: Question[];
  sessionId: string;
}

// Une option « Autre » est convertie en champ de saisie libre : le texte tapé
// devient la réponse (jamais le mot « Autre »), conformément aux bonnes
// pratiques AskUserQuestion du SDK Claude.
function isOtherOption(label: string): boolean {
  return label.toLowerCase().startsWith("autre");
}

// Options d'identification (« Variable stockée (saisir le nom) », « Rubrique du
// bulletin (saisir le code) », « Colonne EVP (saisir le titre) »…) : le libellé
// annonce lui-même une saisie, il faut donc afficher le champ immédiatement
// sans attendre une 2e question.
function isInlineTextOption(label: string): boolean {
  return /\bsaisir\s+(le|la|l')\b/i.test(label);
}

// Toute option qui doit ouvrir un champ de saisie.
function needsTextInput(label: string): boolean {
  return isOtherOption(label) || isInlineTextOption(label);
}

function hasBuiltinOther(q: Question): boolean {
  return q.options.some((o) => isOtherOption(o.label));
}

// Valeur renvoyée à l'agent pour une saisie libre. Pour « Autre » c'est le
// texte seul ; pour une option d'identification on garde le type devant le nom
// saisi ("Variable stockée : mtPaniers") pour que l'agent connaisse à la fois
// la nature de la source et son identifiant exact.
function formatTextAnswer(optionLabel: string, value: string): string {
  const text = value.trim();
  if (!text) return "";
  if (!optionLabel || isOtherOption(optionLabel)) return text;
  const kind = optionLabel.replace(/\s*\(.*\)\s*$/, "").trim();
  return `${kind} : ${text}`;
}

export const ClarificationPanel: React.FC<ClarificationPanelProps> = ({
  questions,
  sessionId: sessionIdProp,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const sessionIdFromStore = useAppSelector((state) => state.aiChat.sessionId);
  const sessionId = sessionIdFromStore ?? sessionIdProp;

  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const q of questions) {
      const firstReal = q.options.find((o) => !needsTextInput(o.label));
      if (firstReal) init[q.question] = firstReal.label;
    }
    return init;
  });

  const [otherActive, setOtherActive] = useState<Record<string, boolean>>({});
  const [otherText, setOtherText] = useState<Record<string, string>>({});
  // Libellé de l'option à saisie libre actuellement cochée, par question :
  // permet d'afficher le champ sous la bonne option quand une même question en
  // propose plusieurs (rubrique / variable stockée / colonne EVP).
  const [textOptionLabel, setTextOptionLabel] = useState<
    Record<string, string>
  >({});
  const [submitting, setSubmitting] = useState(false);

  const isChecked = (q: Question, label: string): boolean => {
    if (needsTextInput(label)) {
      return (
        (otherActive[q.question] ?? false) &&
        textOptionLabel[q.question] === label
      );
    }
    if (q.multiSelect) {
      return answers[q.question]?.split(", ").includes(label) ?? false;
    }
    return answers[q.question] === label && !(otherActive[q.question] ?? false);
  };

  const handleOptionChange = (q: Question, label: string, checked: boolean) => {
    if (needsTextInput(label)) {
      setOtherActive((prev) => ({ ...prev, [q.question]: checked }));
      setTextOptionLabel((prev) => ({
        ...prev,
        [q.question]: checked ? label : "",
      }));
      if (!q.multiSelect) {
        setAnswers((prev) => ({ ...prev, [q.question]: "" }));
      }
      if (!checked) {
        const old = otherText[q.question] ?? "";
        const removed = formatTextAnswer(label, old);
        setOtherText((prev) => ({ ...prev, [q.question]: "" }));
        if (q.multiSelect && removed) {
          const current =
            answers[q.question]?.split(", ").filter(Boolean) ?? [];
          setAnswers((prev) => ({
            ...prev,
            [q.question]: current.filter((l) => l !== removed).join(", "),
          }));
        }
      }
    } else if (q.multiSelect) {
      const current = answers[q.question]?.split(", ").filter(Boolean) ?? [];
      const updated = checked
        ? [...current, label]
        : current.filter((l) => l !== label);
      setAnswers((prev) => ({ ...prev, [q.question]: updated.join(", ") }));
    } else {
      setOtherActive((prev) => ({ ...prev, [q.question]: false }));
      setOtherText((prev) => ({ ...prev, [q.question]: "" }));
      setTextOptionLabel((prev) => ({ ...prev, [q.question]: "" }));
      setAnswers((prev) => ({ ...prev, [q.question]: label }));
    }
  };

  const textAnswerValue = (q: Question, value: string): string =>
    formatTextAnswer(textOptionLabel[q.question] ?? "", value);

  const handleOtherTextChange = (q: Question, value: string) => {
    const old = otherText[q.question] ?? "";
    const oldAnswer = textAnswerValue(q, old);
    const newAnswer = textAnswerValue(q, value);
    setOtherText((prev) => ({ ...prev, [q.question]: value }));
    if (q.multiSelect) {
      const current = answers[q.question]?.split(", ").filter(Boolean) ?? [];
      const withoutOld = current.filter((l) => l !== oldAnswer);
      const updated = newAnswer ? [...withoutOld, newAnswer] : withoutOld;
      setAnswers((prev) => ({ ...prev, [q.question]: updated.join(", ") }));
    } else {
      setAnswers((prev) => ({ ...prev, [q.question]: newAnswer }));
    }
  };

  const hasEmptyOther = questions.some(
    (q) => otherActive[q.question] && !otherText[q.question]?.trim(),
  );

  const handleSubmit = async () => {
    if (!sessionId) {
      dispatch(setError(t("aiAgent.clarification.errorNoSession")));
      return;
    }
    if (hasEmptyOther) {
      dispatch(setError(t("aiAgent.clarification.errorOtherRequired")));
      return;
    }
    setSubmitting(true);
    try {
      await reply(sessionId, answers);
      // Trace visible des clarifications : sans ce message, les questions et
      // les réponses disparaissent à la validation et on ne peut plus
      // comprendre (ni déboguer) pourquoi le code généré est ce qu'il est.
      const recap = [
        t("aiAgent.clarification.recapTitle"),
        ...questions.map((q, i) => {
          const a = answers[q.question]?.trim();
          return `${i + 1}. ${q.question}\n   → ${a || t("aiAgent.clarification.recapNoAnswer")}`;
        }),
      ].join("\n");
      dispatch(
        addMessage({
          id: `clarif-${Date.now()}`,
          role: "user",
          content: recap,
          timestamp: Date.now(),
        }),
      );
      dispatch(completeThinkingStep({ kind: "question" }));
      dispatch(setQuestions(null));
      dispatch(setWaitingForReply(false));
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t("aiAgent.clarification.errorSend");
      dispatch(setError(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-t bg-background/95 flex flex-col max-h-[55%] shrink-0">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-primary/5 shrink-0">
        <ChevronRight className="h-3.5 w-3.5 text-primary shrink-0" />
        <p className="text-xs font-semibold text-primary">
          {t("aiAgent.clarification.header")}
        </p>
      </div>

      <div className="p-4 space-y-5 overflow-y-auto flex-1 min-h-0">
        {questions.map((q, qi) => (
          <div key={q.question} className="space-y-2.5">
            {/* Question label */}
            <div className="flex items-start gap-2">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold shrink-0 mt-px">
                {qi + 1}
              </span>
              <p className="text-sm font-medium leading-snug">{q.question}</p>
            </div>

            {/* Options */}
            <div className="grid gap-1.5 pl-7">
              {q.options.map((opt) => {
                const checked = isChecked(q, opt.label);
                const isOther = needsTextInput(opt.label);
                return (
                  <React.Fragment key={opt.label}>
                    <label
                      className={cn(
                        "relative flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors duration-150 select-none",
                        checked
                          ? "border-primary/40 bg-primary/5 ring-1 ring-primary/20 ring-inset"
                          : "border-border hover:bg-muted/40",
                      )}
                    >
                      {/* Custom checkbox / radio */}
                      <div
                        className={cn(
                          "flex items-center justify-center w-4 h-4 rounded-sm border shrink-0 mt-px transition-colors duration-150",
                          q.multiSelect ? "rounded-sm" : "rounded-full",
                          checked
                            ? "bg-primary border-primary"
                            : "border-border bg-background",
                        )}
                      >
                        {checked && (
                          <Check
                            className="h-2.5 w-2.5 text-primary-foreground"
                            strokeWidth={3}
                          />
                        )}
                      </div>
                      <input
                        type={q.multiSelect ? "checkbox" : "radio"}
                        name={q.question}
                        value={opt.label}
                        checked={checked}
                        onChange={(e) =>
                          handleOptionChange(q, opt.label, e.target.checked)
                        }
                        className="sr-only"
                      />
                      <div className="min-w-0">
                        <span className="text-sm font-medium leading-none">
                          {opt.label}
                        </span>
                        {opt.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                            {opt.description}
                          </p>
                        )}
                      </div>
                    </label>

                    {isOther && checked && (
                      <input
                        type="text"
                        value={otherText[q.question] ?? ""}
                        onChange={(e) =>
                          handleOtherTextChange(q, e.target.value)
                        }
                        placeholder={
                          isOtherOption(opt.label)
                            ? t("aiAgent.clarification.inputPlaceholder")
                            : opt.label
                        }
                        className="ml-7 w-full border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-shadow duration-150"
                      />
                    )}
                  </React.Fragment>
                );
              })}

              {/* Synthetic "Autre" option when not built-in */}
              {!hasBuiltinOther(q) && (
                <>
                  <label
                    className={cn(
                      "relative flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors duration-150 select-none",
                      isChecked(q, "Autre")
                        ? "border-primary/40 bg-primary/5 ring-1 ring-primary/20 ring-inset"
                        : "border-border hover:bg-muted/40",
                    )}
                  >
                    <div
                      className={cn(
                        "flex items-center justify-center w-4 h-4 border shrink-0 mt-px transition-colors duration-150",
                        q.multiSelect ? "rounded-sm" : "rounded-full",
                        isChecked(q, "Autre")
                          ? "bg-primary border-primary"
                          : "border-border bg-background",
                      )}
                    >
                      {isChecked(q, "Autre") && (
                        <Check
                          className="h-2.5 w-2.5 text-primary-foreground"
                          strokeWidth={3}
                        />
                      )}
                    </div>
                    <input
                      type={q.multiSelect ? "checkbox" : "radio"}
                      name={q.question}
                      checked={isChecked(q, "Autre")}
                      onChange={(e) =>
                        handleOptionChange(q, "Autre", e.target.checked)
                      }
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">
                      {t("aiAgent.clarification.other")}
                    </span>
                  </label>

                  {isChecked(q, "Autre") && (
                    <input
                      type="text"
                      value={otherText[q.question] ?? ""}
                      onChange={(e) => handleOtherTextChange(q, e.target.value)}
                      placeholder={t("aiAgent.clarification.inputPlaceholder")}
                      className="ml-7 w-full border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-shadow duration-150"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="px-4 pb-4 shrink-0">
        <Button
          onClick={handleSubmit}
          disabled={submitting || hasEmptyOther}
          className="w-full gap-2 transition-opacity duration-150 cursor-pointer"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("aiAgent.clarification.submitting")}
            </>
          ) : (
            t("aiAgent.clarification.submit")
          )}
        </Button>
      </div>
    </div>
  );
};
